import {AuthClosedReasonMap, AuthorizationInfo, AuthorizationRecordType, AuthorizationRecordTypeMap} from "./domain";
import {TextFieldReadonly} from "../../common/component/TextFieldReadonly";
import {AuthorizationTierDetailSection} from "./AuthorizationTierDetailSection";
import React from "react";


type ClientProfileAuthorizationDetailProps = {
    authorization: AuthorizationInfo | undefined,
    isAssessmentAuth: boolean,
}

export function ClientProfileAuthorizationDetail({
                                                     authorization,
                                                     isAssessmentAuth,
                                                 }: ClientProfileAuthorizationDetailProps) {
    const isPsychoTreatment = [AuthorizationRecordType.Psychotherapy_Treatment].includes(AuthorizationRecordTypeMap.get(authorization?.recordType ?? "") as AuthorizationRecordType);
    const isDEAuth = [AuthorizationRecordType.Diagnostic_Evaluation_Authorization].includes(AuthorizationRecordTypeMap.get(authorization?.recordType ?? "") as AuthorizationRecordType);
    const isTherapyTreatment = [AuthorizationRecordType.Therapy_Treatment_Authorization].includes(AuthorizationRecordTypeMap.get(authorization?.recordType ?? "") as AuthorizationRecordType);

    let tiers: number[] = [];
    if (authorization) {
        tiers = [...Array(authorization.numOfTier + 1).keys()].slice(1);
    }

    return (
        <div
            className={`px-4 pt-2 h-full flex flex-col ${isAssessmentAuth ? "pb-10" : "authorization-detail-modal-content-height"}`}>
            <div className="grid grid-cols-2 gap-1 justify-around w-full">
                <TextFieldReadonly label={"Authorization Number"}
                                   small
                                   valueOpacity={false}
                                   value={authorization?.bhpnAuthNumber ?? ""}
                />
                <TextFieldReadonly label={"Diagnosis"}
                                   value={authorization?.diagnosis ?? "None"}
                                   valueOpacity={false}
                                   small
                />
                <TextFieldReadonly label={"Service Line"}
                                   hide={isAssessmentAuth || isDEAuth || isTherapyTreatment}
                                   noDiv={isTherapyTreatment || isDEAuth}
                                   value={authorization?.serviceLine ?? ""}
                                   valueOpacity={false}
                                   small
                />
                {
                    isTherapyTreatment && therapySpeciality(authorization)
                }
                <TextFieldReadonly label={"Report Due Date"}
                                   hide={isDEAuth}
                                   noDiv={isDEAuth}
                                   value={authorization?.reportDueDate ?? ""}
                                   valueOpacity={false}
                                   small
                                   isDate/>
                {isTherapyTreatment && therapyTreatmentFields(authorization)}
                <TextFieldReadonly label={"Start Date"} value={authorization?.startDate ?? ""}
                                   isDate
                                   valueOpacity={false}
                                   small
                />
                <TextFieldReadonly label={"End Date"}
                                   small
                                   value={authorization?.endDate ?? ""}
                                   valueOpacity={false}
                                   isDate
                />
                {isAssessmentAuth && assessmentFields(authorization)}
                {isPsychoTreatment && psychotherapyTreatmentFields(authorization)}
                {!isAssessmentAuth && !isPsychoTreatment && !isDEAuth && !isTherapyTreatment && numOfTiers(authorization?.numOfTier?.toString() ?? "")}
                {isDEAuth && diagnosticProcedureCodeAndReportDueDate(authorization?.diagnosticEvaluationProcedureCode ?? "", authorization?.reportDueDate ?? "")}

                <StatusDisplay status={authorization?.status ?? ""}/>
                <TextFieldReadonly label={"Closed Reason"}
                                   small
                                   valueOpacity={false}
                                   value={AuthClosedReasonMap.get(authorization?.closedReason ?? "") ?? ""}
                />
            </div>
            {
                !isAssessmentAuth && !isPsychoTreatment && !isDEAuth && !isTherapyTreatment &&
                <div className="grid grid-cols-2 scrollbar-always">
                    {
                        tiers.map((tier) =>
                            <AuthorizationTierDetailSection
                                key={tier}
                                authorization={authorization ?? null} tierNum={tier}/>)
                    }
                </div>
            }
        </div>
    );
}

function numOfTiers(numOfTier: string) {
    return <>
        <TextFieldReadonly label={"Number Of Tier"}
                           small
                           valueOpacity={false}
                           value={numOfTier}
        />
    </>;
}

function therapySpeciality(authorization: AuthorizationInfo | undefined) {
    return (
        <>
            <TextFieldReadonly label={"Speciality"}
                               small
                               valueOpacity={false}
                               value={authorization?.funderAuthSpeciality ?? ""}
            />
        </>
    )
}

function therapyTreatmentFields(authorization: AuthorizationInfo | undefined) {
    return (
        <>
            <TextFieldReadonly label={"Authorized For"}
                               value={`${authorization?.therapyTreatmentUnit?.toString()} ${authorization?.therapyTreatmentUnitType} per ${authorization?.therapyTreatmentUnitsPer}`}
                               valueOpacity={false}
                               small
            />
            <TextFieldReadonly label={"Procedure Code"}
                               small
                               valueOpacity={false}
                               value={authorization?.therapyTreatmentProcedureCode ?? ""}
            />
        </>
    )
}

function diagnosticProcedureCodeAndReportDueDate(diagnosticEvaluationProcedureCode: string, reportDueDate: string) {
    return (
        <>
            <TextFieldReadonly label={"Procedure Code"}
                               small
                               valueOpacity={false}
                               value={diagnosticEvaluationProcedureCode}
            />
            <TextFieldReadonly label={"Report Due Date"}
                               value={reportDueDate}
                               valueOpacity={false}
                               small
                               isDate/>
        </>
    )
}

function psychotherapyTreatmentFields(authorization: AuthorizationInfo | undefined) {
    return (
        <>
            <TextFieldReadonly label={`Therapy Treatment`}
                               value={`${authorization?.therapyTreatmentUnit?.toString()} ${authorization?.therapyTreatmentUnitType} per ${authorization?.therapyTreatmentUnitsPer}`}
                               valueOpacity={false}
                               small
            />
            <TextFieldReadonly label={"Service Type"}
                               small
                               valueOpacity={false}
                               value={authorization?.serviceType1 ?? ""}
            />
        </>
    )
}

function assessmentFields(authorization: AuthorizationInfo | undefined) {
    return (
        <>
            <TextFieldReadonly label={"Assessment Type"}
                               small
                               valueOpacity={false}
                               value={authorization?.allServiceType.join() ?? ""}
            />
            <TextFieldReadonly label={"Assessment Code"}
                               small
                               valueOpacity={false}
                               value={authorization?.assessmentCode ?? ""}
            />
        </>
    )
}

const StatusDisplay = ({status}: { status: string }) => {
    return <li className={`w-full flex text-sm justify-center items-center text-left`}>
        <div className={`text-xs pr-4 text-right w-1/3`}>
            Status
        </div>
        <div className={`w-2/3 p-1 font-bold ${status === "Active" ? "text-green-600" : "text-red-600"}`}>{status}</div>
    </li>
}
